import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { FiGrid, FiHeart } from 'react-icons/fi';
import { theme } from '../theme';
import { activeCommunityLinks } from '../config/social';
import SubscribeButton from './social/SubscribeButton';
import DiscordButton from './social/DiscordButton';
import NewsletterSignup from './NewsletterSignup';
import { t, locale, setLocale, availableLocales } from '../config/i18n';

const FooterWrap = styled.footer`
  background: ${theme.colors.bgSurface};
  border-top: 1px solid ${theme.colors.border};
  padding: 3rem 2rem 1.5rem;
  margin-top: auto;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 2.5rem;

  /* ⚠️ Un palier intermédiaire : à quatre colonnes, « Flux RSS » passait sous
     700px en même temps que les autres, mais entre 700 et 1000px les colonnes
     devenaient trop étroites et les libellés se coupaient en deux lignes. */
  @media (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${theme.fonts.heading};
  font-size: 1.2rem;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: 0.75rem;

  svg { color: ${theme.colors.primary}; }
`;

const Tagline = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
  line-height: 1.6;
`;

const ColTitle = styled.h4`
  font-family: ${theme.fonts.heading};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.9rem;
`;

const ColLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const FooterLink = styled(NavLink)`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover { color: ${theme.colors.primaryLight}; }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.1rem;
`;

const Bottom = styled.div`
  max-width: 1200px;
  margin: 2rem auto 0;
  padding-top: 1.25rem;
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};

  svg { color: ${theme.colors.accent}; font-size: 0.75rem; }
`;

const Legal = styled.div`
  max-width: 1200px; margin: 0.6rem auto 0; padding-top: 0.6rem;
  display: flex; align-items: center; justify-content: center; flex-wrap: wrap;
  gap: 0.4rem 1rem; font-size: 0.78rem;
  a { color: ${theme.colors.textMuted}; text-decoration: none; }
  a:hover { color: ${theme.colors.primaryLight}; }
  span { color: ${theme.colors.border}; }
`;

// Pages légales de l'écosystème (servies par WordPress — source unique). Surchargeable en prod.
const WP_BASE = process.env.REACT_APP_BLOG_URL || 'https://wordpress-3.docker.devhost';
// ── LES FLUX RSS ─────────────────────────────────────────────────────────
// 🔴 User, 02/11/2026 : « je ne vois pas les liens des flux RSS des ressources
// sur l'AppStore, je ne sais pas où ils sont. Ils devraient être mis en avant. »
// Ils existaient côté backend depuis le 02/11 mais RIEN ne les annonçait :
// ni balise `<link rel="alternate">`, ni le moindre lien dans l'interface.
// 🔑 On affiche la langue COURANTE seulement. Lister les dix (5 ressources ×
// 2 langues) dans une colonne de pied de page en ferait un mur illisible, et
// personne ne s'abonne à un mur. Les dix restent accessibles par /rss.
const FLUX = [
  { cle: 'effets', fr: 'Effets', en: 'Effects' },
  { cle: 'palettes', fr: 'Palettes', en: 'Palettes' },
  { cle: 'videos', fr: 'Vidéos', en: 'Videos' },
  { cle: 'jeux', fr: 'Jeux', en: 'Games' },
  { cle: 'chaines', fr: 'Chaînes', en: 'Channels' },
];

const LEGAL = [
  { label: 'Mentions légales', href: `${WP_BASE}/mentions-legales/` },
  { label: 'Confidentialité', href: `${WP_BASE}/politique-de-confidentialite/` },
  { label: 'CGU', href: `${WP_BASE}/conditions-generales-utilisation/` },
];

export default function Footer() {
  return (
    <FooterWrap>
      <Inner>
        <div>
          <Brand><FiGrid /> VertinGo</Brand>
          <Tagline>
            Des jeux gratuits, trois chaînes YouTube et une communauté — réunis en un seul
            écosystème. Joue, regarde, partage, et fais grandir l’aventure avec nous.
          </Tagline>
          <div style={{ marginTop: '1.25rem' }}>
            <NewsletterSignup />
          </div>
        </div>

        <div>
          <ColTitle>Navigation</ColTitle>
          <ColLinks>
            <FooterLink to="/">{t('nav.home')}</FooterLink>
            <FooterLink to="/store">{t('nav.games')}</FooterLink>
            <FooterLink to="/videos">{t('nav.videos')}</FooterLink>
            <FooterLink to="/channels">{t('nav.channels')}</FooterLink>
            <FooterLink to="/communaute">Communauté</FooterLink>
            <FooterLink to="/premium">✨ Premium</FooterLink>
            <FooterLink to="/embed">{t('nav.embed')}</FooterLink>
            <FooterLink to="/about">{t('nav.about')}</FooterLink>
            <FooterLink to="/support">{t('nav.support')}</FooterLink>
          </ColLinks>
        </div>

        <div>
          <ColTitle>Communauté</ColTitle>
          <ColLinks>
            {activeCommunityLinks().map((l) => (
              <FooterLink key={l.key} as="a" href={l.url} target="_blank" rel="noreferrer">
                {l.label}
              </FooterLink>
            ))}
          </ColLinks>
          <CtaRow>
            <SubscribeButton />
            <DiscordButton />
          </CtaRow>
        </div>

        <div>
          <ColTitle>Flux RSS</ColTitle>
          <ColLinks>
            {FLUX.map((f) => (
              <FooterLink
                key={f.cle}
                as="a"
                href={`/rss/${f.cle}.${locale === 'en' ? 'en' : 'fr'}.xml`}
                // ⚠️ Pas de target="_blank" : un flux s'ouvre dans un lecteur,
                // pas dans un onglet qu'on laisse traîner. Et `type` permet au
                // navigateur de proposer l'abonnement plutôt que d'afficher
                // du XML brut.
                type="application/rss+xml"
              >
                {locale === 'en' ? f.en : f.fr}
              </FooterLink>
            ))}
            <FooterLink as="a" href="/rss" style={{ opacity: 0.75 }}>
              {locale === 'en' ? 'All feeds →' : 'Tous les flux →'}
            </FooterLink>
            {/* 🔑 LE LIEN QUI MANQUAIT. Les 49 fiches `/t/<cle>` n'étaient
                désignées QUE par le sitemap : le HTML servi de /palettes ne
                contient aucun `href="/t/..."`, la bibliothèque étant rendue
                côté client. Ce lien, présent sur TOUTES les pages, leur donne
                enfin un chemin qu'un robot sans JavaScript peut suivre. */}
            <FooterLink as="a" href="/t/" style={{ opacity: 0.75 }}>
              {locale === 'en' ? 'All 49 effects →' : 'Les 49 effets →'}
            </FooterLink>
          </ColLinks>
        </div>
      </Inner>

      <Bottom>
        <span style={{ marginRight: '0.75rem' }}>{t('footer.language')}:</span>
        {availableLocales.map((l) => (
          <button
            key={l}
            onClick={() => setLocale(l)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.3rem',
              fontWeight: l === locale ? 700 : 400,
              color: l === locale ? '#9d65f5' : '#64748b', textTransform: 'uppercase', fontSize: '0.75rem',
            }}
          >{l}</button>
        ))}
        <span style={{ margin: '0 0.75rem' }}>·</span>
        Fait avec <FiHeart /> par VertinGo · {new Date().getFullYear()}
      </Bottom>
      <Legal>
        {LEGAL.map((l, i) => (
          <React.Fragment key={l.label}>
            {i > 0 && <span>·</span>}
            <a href={l.href} target="_blank" rel="noreferrer">{l.label}</a>
          </React.Fragment>
        ))}
      </Legal>
    </FooterWrap>
  );
}
